if (!customElements.get('bundle-product-form')) {
  customElements.define(
    'bundle-product-form',
    class BundleProductForm extends HTMLElement {
      constructor() {
        super();

        try {
          /**
           * @type {Bundle}
           */
          this.bundleConfig = JSON.parse(this.dataset.bundleConfig);
        } catch (error) {
          console.error('Invalid bundle configuration.', error);
        }

        // Assign the actual form element inside the custom element
        this.form = this.querySelector('form');
        this.loadingSpinner = this.querySelector('.add-to-cart-container .loading__spinner');
        this.variantSelectorsLoading = this.querySelector('[data-variant-selectors-loading]');
        this.renderVariantSelectorsLoadingRows();
        this.quantityInput = this.querySelector('.quantity__input');
        this.quantityLiveRegion = this.querySelector('[data-quantity-live]');
        this.lastAnnouncedQuantity = this.quantityInput?.value || '';
        this.addToCartMessage = 'Add to Cart';
        this.localizationIsEurope = this.dataset.localizationIsEurope === 'true';
        this.preorderEuNote = this.dataset.preorderEuNote;

        // Ensure that the form exists
        if (!this.form) {
          console.error('Form element not found inside product-form.');
          return;
        }

        // Ensure the variantIdInput exists before using it
        if (!this.variantIdInput) {
          console.error('Variant ID input is missing in the form.');
          return;
        }

        // Attach event listener for form submission
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        // Get cart-drawer or cart-notification
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Submit button setup
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        /**
         * @type {Variant|undefined[]}
         */
        this.selectedVariants = [];
      }

      // Clones the loading-skeleton row
      renderVariantSelectorsLoadingRows() {
        const container = this.variantSelectorsLoading;
        const templateRow = container?.querySelector('[data-variant-selectors-loading-row]');
        if (!container || !templateRow) return;

        const componentCount = this.bundleConfig?.configs?.[0]?.components?.length || 2;

        for (let i = 1; i < componentCount; i++) {
          container.appendChild(templateRow.cloneNode(true));
        }
      }

      /**
       * @type {number}
       */
      cacheExpirationTime = 1000 * 60 * 2; // 2 minutes

      compressString(input) {
        const dictionary = {};
        const data = (input + '').split('');
        const out = [];
        let currChar;
        let phrase = data[0];
        let code = 256;
        for (let i = 1; i < data.length; i++) {
          currChar = data[i];
          if (dictionary[phrase + currChar] != null) {
            phrase += currChar;
          } else {
            out.push(phrase.length > 1 ? dictionary[phrase] : phrase.charCodeAt(0));
            dictionary[phrase + currChar] = code;
            code++;
            phrase = currChar;
          }
        }
        out.push(phrase.length > 1 ? dictionary[phrase] : phrase.charCodeAt(0));
        for (let i = 0; i < out.length; i++) {
          out[i] = String.fromCharCode(out[i]);
        }
        return out.join('');
      }

      async connectedCallback() {
        // Fetch the product data for each component
        const componentVariantIds = this.bundleConfig.configs[0].components
          .map((component) => component.variantIds)
          .flat();
        let compressedParam = this.compressString(componentVariantIds.join(','));

        // URL encode the compressed string
        compressedParam = encodeURIComponent(compressedParam);

        // Use a common cache key format for all components except bundleCartItem
        const country = window.Shopify.country;
        const cacheKey = `wmxb_bcd_${compressedParam}_${country}`;
        const inProgressKey = `${cacheKey}_in_progress`;

        // Check if we have cached data
        const cachedData = sessionStorage.getItem(cacheKey);
        // Check if there's a request in progress
        const inProgress = window[inProgressKey];
        let components;

        if (cachedData) {
          // Check if the cached data has expired
          const expiration = sessionStorage.getItem(`${cacheKey}_expiration`);
          if (expiration && Date.now() < parseInt(expiration)) {
            // Use cached data if available and not expired
            components = JSON.parse(cachedData);
          } else {
            // If there's a request in progress, wait for it to complete
            if (inProgress) {
              components = await this.waitForCachedData(cacheKey, inProgressKey);
            } else {
              // Mark this request as in progress
              window[inProgressKey] = true;

              try {
                // Fetch new data if cached data has expired
                let response = await fetch(`/a/wmxb/bcd?c=${compressedParam}&country=${country}`);
                /**
                 * @type {Product[]}
                 */
                components = await response.json();

                // Cache the response for 2 minutes
                sessionStorage.setItem(cacheKey, JSON.stringify(components));

                // Set expiration time (2 minutes)
                const newExpiration = Date.now() + this.cacheExpirationTime;
                sessionStorage.setItem(`${cacheKey}_expiration`, newExpiration.toString());
              } catch (error) {
                console.error('Error fetching bundle component data:', error);
              } finally {
                // Remove the in-progress flag
                window[inProgressKey] = false;
              }
            }
          }
        } else {
          // If there's a request in progress, wait for it to complete
          if (inProgress) {
            components = await this.waitForCachedData(cacheKey, inProgressKey);
          } else {
            // Mark this request as in progress
            window[inProgressKey] = true;

            try {
              // Fetch new data if not in cache
              let response = await fetch(`/a/wmxb/bcd?c=${compressedParam}&country=${country}`);
              /**
               * @type {Product[]}
               */
              components = await response.json();

              // Cache the response for 2 minutes
              sessionStorage.setItem(cacheKey, JSON.stringify(components));

              // Set expiration time (2 minutes)
              const expiration = Date.now() + this.cacheExpirationTime;
              sessionStorage.setItem(`${cacheKey}_expiration`, expiration.toString());
            } catch (error) {
              console.error('Error fetching bundle component data:', error);
            } finally {
              // Remove the in-progress flag
              window[inProgressKey] = false;
            }
          }
        }

        if (!components) {
          console.error('Failed to fetch component data');
          this.variantSelectorsLoading?.classList.add('hidden');
          this.submitButton.classList.remove('loading');
          this.loadingSpinner.classList.add('hidden');
          return;
        }

        // set each component's position value according to the order in the bundle config
        components.forEach((component) => {
          const matchedComponent = this.bundleConfig.configs[0].components.find(
            (configComponent) => configComponent.productId === component.id
          );
          // get index of the matched component in the bundle config
          const position = this.bundleConfig.configs[0].components.indexOf(matchedComponent);
          component.position = position;
        });

        // Sort the components by position
        components.sort((a, b) => a.position - b.position);

        this.components = components;
        // Create boolean to determine whether multiple pre-order products exist in a bundle (NOTE: this is based bundleConfig components & is separate from this.components)
        this.multiPreOrderBundle = this.hasMultiplePreOrderItems(this?.bundleConfig?.configs[0]?.components);
        this.checkPreorderStatus();
        this.constructVariantSelectors();
        this.updateMaxQuantity();
        this.initQuantityAnnouncements();
      }

      /**
       * Wait for cached data to be available
       * @param {string} cacheKey - The cache key to check
       * @param {string} inProgressKey - The in-progress key to check
       * @param {number} maxWaitTime - Maximum wait time in milliseconds
       * @param {number} checkInterval - Interval between checks in milliseconds
       * @returns {Promise<Product[]>}
       */
      async waitForCachedData(cacheKey, inProgressKey, maxWaitTime = 5000, checkInterval = 100) {
        const startTime = Date.now();

        while (Date.now() - startTime < maxWaitTime) {
          // Check if the data is now available
          const cachedData = sessionStorage.getItem(cacheKey);
          const stillInProgress = window[inProgressKey];

          if (cachedData) {
            // Data is available, use it
            return JSON.parse(cachedData);
          }

          if (!stillInProgress) {
            // The request is no longer in progress but data is not available
            // This likely means the request failed
            break;
          }

          // Wait before checking again
          await new Promise((resolve) => setTimeout(resolve, checkInterval));
        }

        // If we get here, either the wait timed out or the request failed
        // Proceed with our own request
        try {
          const componentVariantIds = this.bundleConfig.configs[0].components
            .map((component) => component.variantIds)
            .flat();
          let compressedParam = this.compressString(componentVariantIds.join(','));
          compressedParam = encodeURIComponent(compressedParam);
          const country = window.Shopify.country;

          window[inProgressKey] = true;

          let response = await fetch(`/a/wmxb/bcd?c=${compressedParam}&country=${country}`);
          const components = await response.json();

          // Cache the response
          sessionStorage.setItem(cacheKey, JSON.stringify(components));

          // Set expiration time (2 minutes)
          const expiration = Date.now() + this.cacheExpirationTime;
          sessionStorage.setItem(`${cacheKey}_expiration`, expiration.toString());

          return components;
        } catch (error) {
          console.error('Error fetching bundle component data after waiting:', error);
          return null;
        } finally {
          // Remove the in-progress flag
          window[inProgressKey] = false;
        }
      }

      initQuantityAnnouncements() {
        if (!this.quantityInput || !this.quantityLiveRegion) return;

        const announceQuantity = () => {
          const currentValue = this.quantityInput.value;
          if (!currentValue || currentValue === this.lastAnnouncedQuantity) return;

          this.lastAnnouncedQuantity = currentValue;
          const prefix = this.quantityLiveRegion.dataset.quantityLivePrefix || 'Quantity';

          this.quantityLiveRegion.textContent = '';
          window.requestAnimationFrame(() => {
            this.quantityLiveRegion.textContent = `${prefix}: ${currentValue}`;
          });
        };

        this.quantityInput.addEventListener('input', announceQuantity);
        this.quantityInput.addEventListener('change', announceQuantity);

        this.querySelectorAll('.quantity__button').forEach((button) => {
          button.addEventListener('click', () => {
            window.requestAnimationFrame(announceQuantity);
          });
        });
      }

      constructVariantSelectors() {
        const variantSelectorsContainer = this.querySelector('.variant-selectors');
        const selectedOptions = {};

        this.components.forEach((component, componentIndex) => {
          const componentContainer = document.createElement('div');
          componentContainer.classList.add('component-container');
          componentContainer.setAttribute('role', 'group');
          componentContainer.setAttribute('aria-labelledby', `component-title-${componentIndex}`);

          const componentImgContainer = document.createElement('div');
          componentImgContainer.classList.add('component-img-container');
          const componentThumbnail = document.createElement('img');
          componentThumbnail.classList.add('component-thumbnail');
          const originalSource = component.media?.edges[0]?.node?.image?.originalSrc;
          if (originalSource) {
            const smallUrl = new URL(originalSource);
            smallUrl.searchParams.set('width', '135');
            smallUrl.searchParams.set('height', '135');
            smallUrl.searchParams.set('crop', 'center');
            componentThumbnail.src = smallUrl.href;
          } else {
            componentThumbnail.src = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
          }

          componentImgContainer.appendChild(componentThumbnail);
          componentContainer.appendChild(componentImgContainer);

          const componentTitle = document.createElement('span');
          componentTitle.classList.add('component-title');
          componentTitle.id = `component-title-${componentIndex}`;
          componentTitle.textContent = component.title;

          const componentQuantity = document.createElement('span');
          // Get the matching component from the bundle config
          const configComponent = this.bundleConfig.configs[0].components[componentIndex];

          const today = new Date();

          if (
            this.multiPreOrderBundle &&
            configComponent?.preOrderSettings?.pre_order_visible &&
            new Date(configComponent.preOrderSettings?.pre_order_date) > today
          ) {
            const componentTitleContainer = document.createElement('div');
            componentTitleContainer.classList.add('component-title-container');
            const componentPreOrder = document.createElement('span');
            componentTitleContainer.append(componentTitle, componentPreOrder);
            componentContainer.appendChild(componentTitleContainer);
            componentPreOrder.classList.add('preorder-message');
            componentPreOrder.textContent = this.createPreOrderMessage(configComponent?.preOrderSettings);
          } else {
            componentContainer.appendChild(componentTitle);
          }

          componentQuantity.classList.add('component-quantity');
          componentQuantity.textContent = `Qty: ${configComponent.quantityOptions[0].quantity}`;
          componentContainer.appendChild(componentQuantity);

          if (componentTitle.length > 16) {
            componentImgContainer.classList.add('long-title');
            componentTitle.classList.add('long-title');
            componentQuantity.classList.add('long-title');
          }

          const optionsContainer = document.createElement('div');
          optionsContainer.classList.add('options-container');

          component.options.edges.forEach((optionEdge, optionIndex) => {
            const option = optionEdge.node;
            const optionContainer = document.createElement('fieldset');
            optionContainer.classList.add('option-container');
            optionContainer.dataset.componentIndex = componentIndex;
            optionContainer.style.border = '0';
            optionContainer.style.padding = '0';
            optionContainer.style.margin = '0';
            optionContainer.style.minInlineSize = '0';

            if (option.values.length === 1 && option.values[0] === 'Default Title') {
              optionContainer.classList.add('hidden');
            }

            const optionLegend = document.createElement('legend');
            optionLegend.classList.add('option__label');

            const srPrefix = document.createElement('span');
            srPrefix.classList.add('visually-hidden');
            srPrefix.textContent = `${component.title} - `;

            const legendText = document.createElement('span');
            legendText.textContent = option.name === 'Title' ? component.title : option.name;

            optionLegend.append(srPrefix, legendText);

            if (this.dataset.variantLabelsEnabled !== 'true' || option.name === 'Title') {
              optionLegend.classList.add('visually-hidden');
              optionLegend.textContent = `${component.title} - ${option.name}`;
            }

            optionContainer.appendChild(optionLegend);

            const variantButtons = document.createElement('div');
            variantButtons.classList.add('variant-buttons');
            variantButtons.dataset.optionIndex = optionIndex;

            option.values.forEach((value) => {
              const button = document.createElement('button');
              button.type = 'button';
              button.classList.add('variant-button');
              button.dataset.optionValue = value;
              button.setAttribute('aria-pressed', 'false');

              const buttonText = value === 'Default Title' ? component.title : value;
              button.setAttribute('aria-label', `${component.title} ${option.name}: ${buttonText}`);
              button.textContent = buttonText;

              // if there's only one option value, select it by default
              if (option.values.length === 1) {
                button.setAttribute('aria-pressed', 'true');
                button.classList.add('selected');
                selectedOptions[`${componentIndex}-${optionIndex}`] = value;
              } else {
                button.addEventListener('click', (event) => {
                  const optionIndex = event.target.closest('.variant-buttons').dataset.optionIndex;
                  const optionValue = event.target.dataset.optionValue;

                  event.target
                    .closest('.variant-buttons')
                    .querySelectorAll('button')
                    .forEach((btn) => {
                      btn.setAttribute('aria-pressed', 'false');
                      btn.classList.remove('selected'); // Remove visual selection
                    });
                  
                  // Set aria-selected true for the selected button and mark visually
                  event.target.setAttribute('aria-pressed', 'true');
                  event.target.classList.add('selected'); // Add visual selection

                  selectedOptions[`${componentIndex}-${optionIndex}`] = optionValue;
                  this.updateVariantSelection(selectedOptions);
                  this.updateOptionsAvailability(selectedOptions);
                });
              }
              variantButtons.appendChild(button);
            });

            optionContainer.appendChild(variantButtons);
            optionsContainer.appendChild(optionContainer);
          });

          componentContainer.appendChild(optionsContainer);
          variantSelectorsContainer.appendChild(componentContainer);
        });

        // Child items are now in the DOM - hide skeleton.
        this.variantSelectorsLoading?.classList.add('hidden');
        this.updateVariantSelection(selectedOptions);
        this.updateOptionsAvailability(selectedOptions);
        const someComponentsSoldOut = this.components.some((component) => component.soldOut);
        if (someComponentsSoldOut) {
          this.toggleSubmitButton(true, 'Sold Out');
          const priceComponent = document.querySelector('product-info .price');
          if (priceComponent) priceComponent.classList.add('price--sold-out');
        } else if (this.bundleConfig.status == 'active') {
          const allComponentsSelected = this.selectedVariants.every((variant) => variant !== undefined);
          this.toggleSubmitButton(
            !allComponentsSelected,
            allComponentsSelected ? this.addToCartMessage : 'Select Options'
          );
        }

        this.submitButton.classList.remove('loading');
        this.loadingSpinner.classList.add('hidden');
      }

      updateVariantSelection(selectedOptions) {
        // Find the selected variants of each component
        const selectedVariants = this.components.map((component, componentIndex) => {
          const selectedVariant = component.variants.find((variant) => {
            const optionValues = variant.selectedOptions.map((option) => option.value);
            return optionValues.every((value, index) => {
              return value === selectedOptions[`${componentIndex}-${index}`];
            });
          });

          return selectedVariant;
        });

        this.selectedVariants = selectedVariants;

        // Update the price of the current component variant selector
        const componentPriceElements = this.querySelectorAll('.component-price');
        componentPriceElements.forEach((priceElement, index) => {
          if (selectedVariants[index] === undefined) return;
          priceElement.textContent = `$${selectedVariants[index].price}`;
        });

        // Update a hidden input with the selected variant IDs and adds a line item property called _bundle_components
        // Get the component quantities from the bundle config and add it to an object structured as {variantId: quantity}
        const componentQuantities = this.bundleConfig.configs[0].components.reduce((acc, component) => {
          const selectedVariant = selectedVariants.find((variant) => variant?.productId === component.productId);
          if (!selectedVariant) return acc;
          const quantity = component.quantityOptions[0].quantity;
          acc[selectedVariant.id] = quantity;
          return acc;
        }, {});

        this.bundleComponentsInput.value = JSON.stringify(componentQuantities);

        // Keep bundle stock line properties in sync with the current selection
        this.renderStockLineProperties();

        // Update urgency message based on bundle component inventory
        this.updateUrgencyMessage(selectedVariants);

        // Enable or disable the submit button based on whether all components have a selection made
        const allComponentsSelected = selectedVariants.every((variant) => variant !== undefined);

        // If any components are sold out, disable the submit button
        const soldOutComponents = this.components.some((component) => component.soldOut);
        if (soldOutComponents) {
          this.toggleSubmitButton(true, 'Sold Out');

          // Disable all variant buttons
          const variantButtons = this.querySelectorAll('.variant-button');
          variantButtons.forEach((button) => {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
          });
        } else {
          this.toggleSubmitButton(
            !allComponentsSelected,
            allComponentsSelected ? this.addToCartMessage : 'Select Options'
          );
        }
      }

      updateOptionsAvailability(selectedOptions) {
        if (this.bundleConfig.status != 'active') {
          this.toggleSubmitButton(true, 'Unavailable');

          // Disable all variant buttons
          const variantButtons = this.querySelectorAll('.variant-button');
          variantButtons.forEach((button) => {
            // De-select the button
            button.classList.remove('selected');
            button.setAttribute('aria-pressed', 'false');
            button.setAttribute('disabled', 'disabled');
            button.setAttribute('aria-disabled', 'true');
          });
          // Update mobile sticky ATC
          const stickyATCButton = document.querySelector('[data-stikcy-atc-wrapper] button');
          if (stickyATCButton) {
            stickyATCButton.textContent = 'Unavailable';
            stickyATCButton.disabled = true;
          }
          return;
        }

        // Iterate over each component
        this.components.forEach((component, componentIndex) => {
          // Iterate over each option of the component

          component.options.edges.forEach((optionEdge, optionIndex) => {
            const option = optionEdge.node;
            // Select all variant buttons for the current option
            const variantButtons = this.querySelectorAll(
              `.option-container[data-component-index="${componentIndex}"] .variant-buttons[data-option-index="${optionIndex}"] button`
            );

            // Iterate over each variant button
            variantButtons.forEach((button) => {
              const optionValue = button.dataset.optionValue;
              // Check if the option value is available in any variant
              const isAvailable = component.variants.some((variant) => {
                const configComponent = this.bundleConfig.configs[0].components.find(
                  (component) => component.productId === variant.productId
                );
                const componentQuantity = configComponent?.quantityOptions[0]?.quantity || 1;
                const variantAvailable =
                  variant.inventoryQuantity >= componentQuantity ||
                  variant.inventoryPolicy === 'continue' ||
                  variant?.inventoryItem?.tracked == false;

                const optionValues = variant.selectedOptions.map((opt) => opt.value);
                return (
                  optionValues.every((value, index) => {
                    // Check if the current option value matches the selected option value
                    if (index === optionIndex) {
                      return value === optionValue;
                    }
                    // If no option is selected for the current index, make it clickable
                    if (selectedOptions[`${componentIndex}-${index}`] === undefined) {
                      return true;
                    }
                    // Check if the selected option value matches the variant option value
                    return value === selectedOptions[`${componentIndex}-${index}`];
                  }) && variantAvailable // Ensure the variant is in stock
                );
              });

              // Enable or disable the button based on availability
              if (isAvailable) {
                button.removeAttribute('disabled');
                button.removeAttribute('aria-disabled');
              } else {
                button.setAttribute('disabled', 'disabled');
                button.setAttribute('aria-disabled', 'true');

                // If the button is selected, deselect it
                if (button.classList.contains('selected')) {
                  button.classList.remove('selected');
                  button.setAttribute('aria-pressed', 'false');

                  // Update selectedOptions with the deselected option value
                  selectedOptions[`${componentIndex}-${optionIndex}`] = undefined;
                }
              }
            });

            // If every button is disabled, mark the component as sold out
            const isSoldOut = Array.from(variantButtons).every((button) => button.disabled === true);
            if (isSoldOut) {
              component.soldOut = true;
            }
          });
        });
        this.updateVariantSelection(selectedOptions);
      }

      updateMaxQuantity() {
        const maxQuantity = this.selectedVariants.reduce((acc, selectedVariant) => {
          if (!selectedVariant) return acc;

          /**
           * @type {Variant}
           */
          const variant = selectedVariant;

          if (variant.inventoryItem?.tracked == false) return acc;

          // Find the required quantity for the component associated with the variant
          const component = this.bundleConfig.configs[0].components.find(
            (component) => component.productId === variant.productId
          );
          const requiredQuantity = component.quantityOptions[0].quantity;

          if (variant.inventoryQuantity < requiredQuantity && variant.inventoryPolicy === 'continue') return acc;
          return Math.min(acc, Math.floor(variant.inventoryQuantity / requiredQuantity));
        }, Infinity);

        this.quantityInput.disabled = maxQuantity === 0;
        this.quantityInput.max = maxQuantity;
      }

      checkPreorderStatus() {
        // Check if any component is has pre-order settings.
        const preOrderComponents = this.bundleConfig.configs[0].components.filter(
          (component) => component?.preOrderSettings?.pre_order_visible
        );

        // If multiple components have pre-order settings, use the product with the latest pre-order date to display the message/delivery date
        const latestPreOrderComponent = preOrderComponents.reduce((acc, component) => {
          if (!acc) return component;
          return new Date(component.preOrderSettings.pre_order_date) > new Date(acc.preOrderSettings.pre_order_date)
            ? component
            : acc;
        }, null);

        const today = new Date();
        const latestPreOrderDate = new Date(latestPreOrderComponent?.preOrderSettings?.pre_order_date);

        if (
          latestPreOrderComponent &&
          latestPreOrderDate > today &&
          latestPreOrderComponent?.preOrderSettings?.pre_order_visible
        ) {
          // Add hidden preorder inputs to the form
          const isPreOrderInput = document.createElement('input');
          isPreOrderInput.type = 'hidden';
          isPreOrderInput.name = 'properties[__is_preorder]';
          isPreOrderInput.value = 'true';
          this.form.prepend(isPreOrderInput);

          const preOrderReleasesOnInput = document.createElement('input');
          preOrderReleasesOnInput.type = 'hidden';
          preOrderReleasesOnInput.name = 'properties[Ships]';
          preOrderReleasesOnInput.value = new Date(
            latestPreOrderComponent.preOrderSettings.pre_order_date
          ).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            timeZone: 'UTC',
          });
          this.form.prepend(preOrderReleasesOnInput);

          // Update the submit button text to reflect the pre-order status
          this.addToCartMessage = `Pre-Order Now`;
          this.submitButtonText.textContent = this.addToCartMessage;

          // Display the pre-order message for bundles with a SINGLE pre-order item
          const preOrderMessage = document.createElement('div');
          this.multiPreOrderBundle
            ? preOrderMessage.classList.add('hidden')
            : preOrderMessage.classList.add('preorder-message');
          const preOrderText = document.createElement('p');
          preOrderText.textContent = this.createPreOrderMessage(latestPreOrderComponent?.preOrderSettings);

          if (this.localizationIsEurope && this.preorderEuNote) {
            const preOrderEuNote = document.createElement('span');
            preOrderEuNote.classList.add('preorder-note');

            preOrderEuNote.textContent = this.preorderEuNote;
            preOrderText.appendChild(preOrderEuNote);
          }

          preOrderMessage.appendChild(preOrderText);
          this.parentElement.insertBefore(preOrderMessage, this);
        }
      }

      // Form submit handler
      onSubmitHandler(evt) {
        evt.preventDefault(); // Prevent the default form submission
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // if (this.quantityInput.value > this.quantityInput.max) {
        //   this.handleErrorMessage('The quantity you selected is not available.');
        //   return;
        // }

        // Indicate the form is being submitted
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.loadingSpinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.renderStockLineProperties();

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              let errorMessage = response.description || response.message;
              if (response.status === 422) {
                errorMessage = `${errorMessage}\nIf the product is in your cart, please remove it and try again.`;
              }

              this.handleErrorMessage(errorMessage);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Publish cart update event
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.loadingSpinner.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.form.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (text) {
            this.submitButtonText.textContent = text;
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      // Update urgency message based on bundle component inventory
      updateUrgencyMessage(selectedVariants) {
        const urgencyMessageEl = document.querySelector(`#UrgencyMessage-${this.dataset.sectionId}`);
        if (!urgencyMessageEl) {
          return;
        }

        const urgencyThreshold = parseInt(urgencyMessageEl.dataset.urgencyThreshold);

        // Check if ANY selected child variant meets urgency criteria
        let showUrgency = false;

        for (const variant of selectedVariants) {
          if (!variant) {
            continue;
          }

          // Check if variant has Shopify inventory tracking
          if (variant.inventoryItem && variant.inventoryItem.tracked) {
            if (variant.inventoryQuantity > 0 && variant.inventoryQuantity <= urgencyThreshold) {
              showUrgency = true;
              break;
            }
          }
        }

        urgencyMessageEl.style.display = showUrgency ? 'block' : 'none';
      }

      // Accepts a preOrderConfig object & captures the configured pre-order date. Returns a formatted string (i.e. Pre-Order Message)
      createPreOrderMessage(preOrderSettings) {
        const locale = preOrderSettings.pre_order_date_format == 'DD Month YYYY' ? 'en-GB' : 'en-US';
        const configuredDateFormat = this.createConfiguredDateObject(preOrderSettings?.pre_order_date_format);

        const preOrderFormattedDate = new Date(preOrderSettings?.pre_order_date).toLocaleDateString(
          locale,
          configuredDateFormat
        );

        return `Ships: ${preOrderFormattedDate}`;
      }

      // Accepts a string & returns an Object to be used for date configs based on the configured date format
      createConfiguredDateObject = (configuredFormat) => {
        switch (configuredFormat) {
          case 'MM/YYYY':
            return { month: '2-digit', year: 'numeric', timeZone: 'UTC' };

          case 'DD Month YYYY':
            return { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' };

          case 'MM/DD/YYYY':
          default:
            return { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC' }; // Default format
        }
      };

      // Evaluates whether a bundle contains multiple pre-order Items & returns a boolean value
      hasMultiplePreOrderItems(bundleItemsArray) {
        if (bundleItemsArray.length < 2) {
          return false;
        }

        let count = 0;
        return bundleItemsArray.some((obj) => {
          if (obj?.preOrderSettings) {
            count++;
            return count > 1;
          }
          return false;
        });
      }

      // Use the getter for variantIdInput
      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      // Use the getter for bundle components line item property
      get bundleComponentsInput() {
        return this.form.querySelector('.bundle-components-input');
      }

      /**
       * Hidden line-item properties used as cart line attributes in Shopify Functions RunInput.
       */
      ensureStockLinePropertyInputs() {
        if (!this.form) return;

        const currencyInput = this.form.querySelector('input[name="attributes[_storeCurrency]"]');
        const marketInput = this.form.querySelector('input[name="attributes[_storeMarketHandle]"]');
        const anchor = currencyInput || marketInput;

        //properties[available stock]
        if (!this.availableStockInput) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[_stk]';
          input.value = '[]';

          if (anchor) {
            anchor.insertAdjacentElement('afterend', input);
          } else {
            this.form.appendChild(input);
          }
        }

        //properties[safety stock]
        if (!this.safetyStockInput) {
          const target = this.availableStockInput || anchor;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[_ss]';
          input.value = '[]';

          if (target) {
            target.insertAdjacentElement('afterend', input);
          } else {
            this.form.appendChild(input);
          }
        }
      }

      get availableStockInput() {
        if (!this.form) return null;
        return this.form.querySelector('input[name="properties[_stk]"]');
      }

      get safetyStockInput() {
        if (!this.form) return null;
        return this.form.querySelector('input[name="properties[_ss]"]');
      }

      getVariantMetafieldInt(variant, namespace, key) {
        if (!variant) return null;

        const normalize = (raw) => {
          if (raw === null || raw === undefined) return null;

          if (typeof raw === 'object' && raw && Object.prototype.hasOwnProperty.call(raw, 'value')) {
            raw = raw.value;
          }

          if (raw === null || raw === undefined) return null;

          const n = Number(raw);
          return Number.isFinite(n) ? n : null;
        };

        // Array form: [{namespace, key, value}]
        if (Array.isArray(variant.metafields)) {
          const hit = variant.metafields.find((m) => m && m.namespace === namespace && m.key === key);
          if (hit) return normalize(hit.value);
        }

        // Object form: { custom: { available_quantity: ... } }
        if (variant.metafields && typeof variant.metafields === 'object') {
          const ns = variant.metafields[namespace];
          if (ns && typeof ns === 'object') {
            if (Object.prototype.hasOwnProperty.call(ns, key)) return normalize(ns[key]);
          }
        }

        // Flat key fallback: variant["custom.available_quantity"]
        const flatKey = `${namespace}.${key}`;
        if (Object.prototype.hasOwnProperty.call(variant, flatKey)) {
          return normalize(variant[flatKey]);
        }

        return null;
      }

      normalizeVariantIdForProperties(variantId) {
        if (variantId === null || variantId === undefined) return '';
        return String(variantId);
      }

      /**
       * Renders line-item properties so Shopify Functions can read them via RunInput.
       * Called during selection changes and again right before form serialization.
       */
      renderStockLineProperties() {
        this.ensureStockLinePropertyInputs();

        const selected = Array.isArray(this.selectedVariants) ? this.selectedVariants : [];

        const availablePairs = [];
        const safetyPairs = [];

        selected.forEach((variant) => {
          if (!variant || !variant.id) return;

          const available = this.getVariantMetafieldInt(variant, 'custom', 'available_quantity');
          const safety = this.getVariantMetafieldInt(variant, 'custom', 'safety_stock');
          const vid = this.normalizeVariantIdForProperties(variant.id);

          availablePairs.push([vid, available]);
          safetyPairs.push([vid, safety]);
        });

        if (this.availableStockInput) this.availableStockInput.value = JSON.stringify(availablePairs);
        if (this.safetyStockInput) this.safetyStockInput.value = JSON.stringify(safetyPairs);
      }
    }
  );
}

// Types for Bundle Configuration

/**
 * @typedef {Object} QuantityOption
 * @property {number} quantity - The quantity of the component.
 * @property {string} label - The label for the quantity option.
 */

/**
 * @typedef {Object} preOrderSettings
 * @property {string} product_title - The title of the product.
 * @property {string} pre_order_date - The pre-order date of the product.
 * @property {string} pre_order_date_format - The pre-order date format of the product.
 * @property {boolean} pre_order_visible - Indicates if the pre-order is visible.
 * @property {string} pre_order_visible_date - The pre-order visible date of the product.
 */

/**
 * @typedef {Object} Component
 * @property {string} id - The unique identifier for the component.
 * @property {string} productId - The product ID associated with the component.
 * @property {string[]} variantIds - The variant IDs associated with the component.
 * @property {boolean} isMusic - Indicates if the component is music-related.
 * @property {number} minimumPrice - The minimum price for the component.
 * @property {QuantityOption[]} quantityOptions - The available quantity options for the component.
 * @property {boolean|undefined} soldOut - Indicates if the component is sold out. This is set dynamically, not sourced from the config itself.
 * @property {string|null} artistId - The artist associated with the component.
 * @property {string|null} isrc - The ISRC associated with the component.
 * @property {boolean|null} isDigitalDownload - Indicates if the component is a digital download.
 * @property {"S"|"A"|"V"|null} downloadType - The download type of the component. (Single, Album, Video)
 * @property {boolean|null} chartEligible - Indicates if the component is chart-eligible.
 * @property {boolean|null} containsMusic - Indicates if the component contains music.
 * @property {string|null} recordLabel - The record label associated with the component.
 * @property {number|null} ppd - The PPD of the component.
 * @property {preOrderSettings} preOrderSettings - The pre-order settings of the component.
 *
 */

/**
 * @typedef {Object} Config
 * @property {string} title - The title of the configuration.
 * @property {Component[]} components - The components included in the configuration.
 */

/**
 * @typedef {Object} Bundle
 * @property {string} title - The title of the product.
 * @property {"active"|"draft"|"archived"} status - The status of the bundle.
 * @property {number} fixedPriceAmount - The fixed price amount for the product.
 * @property {boolean} useFixedPrice - Indicates if the product uses a fixed price.
 * @property {string} parentProductHandle - The handle of the parent product.
 * @property {Config[]} configs - The configurations available for the product.
 */

// Types for fetched product component data

/**
 * @typedef {Object} Image
 * @property {string} alt - The alternative text for the media.
 * @property {string|null} originalSrc - The original source URL of the media.
 * @property {number} width - The width of the media.
 * @property {number} height - The height of the media.
 */

/**
 * @typedef {Object} MediaNode
 * @property {Image} image - The image object.
 */

/**
 * @typedef {Object} MediaEdge
 * @property {MediaNode} node - The media node.
 */

/**
 * @typedef {Object} Media
 * @property {MediaEdge[]} edges - The edges containing media nodes.
 */

/**
 * @typedef {Object} ProductOptionNode
 * @property {string} name - The name of the product option.
 * @property {number} position - The position of the product option.
 * @property {string[]} values - The values of the product option.
 */

/**
 * @typedef {Object} ProductOptionEdge
 * @property {ProductOptionNode} node - The product option node.
 */

/**
 * @typedef {Object} ProductOption
 * @property {ProductOptionEdge[]} edges - The edges containing product option nodes.
 */

/**
 * @typedef {Object} InventoryItem
 * @property {boolean} tracked - Indicates if the inventory is tracked for the item.
 */

/**
 * @typedef {Object} Variant
 * @property {string} id - The unique identifier for the variant.
 * @property {string} title - The title of the variant.
 * @property {Media} media - The media associated with the variant.
 * @property {number} inventoryQuantity - The inventory quantity of the variant.
 * @property {string} inventoryPolicy - How zero inventory is handled for the variant. ("continue"|"deny")
 * @property {InventoryItem} inventoryItem - The inventory item associated with the variant.
 * @property {string} price - The price of the variant.
 * @property {string|null} compareAtPrice - The compare at price of the variant.
 * @property {string|null} preorderStock - The preorder stock of the variant.
 * @property {string} productId - The product ID associated with the variant.
 * @property {string[]} selectedOptions - The selected options of the variant.
 * @property {{namespace: string, key: string, value: (string|null)}[]|null} metafields - Variant metafields included by the BCD endpoint.
 * @property {string} __typename - The typename of the variant.
 */

/**
 * @typedef {Object} Product
 * @property {string} id - The unique identifier for the product.
 * @property {string} title - The title of the product.
 * @property {string} handle - The handle of the product.
 * @property {string[]} tags - The tags associated with the product.
 * @property {number} minimumPrice - The minimum price of the product.
 * @property {string|null} preOrderReleaseDate - The pre-order release date of the product.
 * @property {ProductOption} options - The options available on the product.
 * @property {Media} media - The media associated with the product.
 * @property {string} vendor - The vendor of the product.
 * @property {string} __typename - The typename of the product.
 * @property {Variant[]} variants - The variants associated with the product.
 */
